<script setup lang="ts">
/**
 * Выпуск ключа доступа — макет «Настройки · Интеграции — выпуск ключа»
 * (203:995) и «— ключ показан один раз» (204:1105), DEV-1566.
 *
 * Два состояния в одном диалоге: форма и показ значения. Разводить их по
 * экранам нельзя — значение показывается ровно один раз, и человек не должен
 * его потерять на переходе.
 *
 * Пресет проставлен заранее: подключить агента — самый частый повод сюда
 * прийти, и заставлять человека собирать набор прав руками значит заставлять
 * его угадывать, какие из них нужны.
 */
import { AppButton, AppCheckbox, AppModal, AppSegmentedControl, AppTextField, useToast } from '@vinprop/design-system'
import { Check, Copy } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { errorMessage } from '@/api/errors'
import {
  createApiKey,
  KEY_SCOPES,
  KEY_TTLS,
  type KeyScope,
  type KeyTtl,
} from '@/features/settings/integrations/api'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ 'update:open': [boolean]; issued: [] }>()

const { t } = useI18n()
const toast = useToast()

/** Пресет MCP: тот же набор, что отдаёт auth по API_KEY_PRESET_MCP. */
const MCP_PRESET: KeyScope[] = ['tracker:read', 'tracker:write', 'knowledge:read']

const name = ref('')
const ttl = ref<KeyTtl>('API_KEY_TTL_90_DAYS')
const scopes = ref<KeyScope[]>([...MCP_PRESET])
const isSaving = ref(false)
const secret = ref('')
const copied = ref(false)

const ttlOptions = computed(() =>
  KEY_TTLS.map((value) => ({ value, label: t(`settings.integrations.dialog.ttl.${value}`) })),
)

/** Ключ без единого права бесполезен: пересечение с правами даст пусто. */
const canSubmit = computed(() => name.value.trim().length > 0 && scopes.value.length > 0)

const toggleScope = (scope: KeyScope, checked: boolean) => {
  scopes.value = checked
    ? [...scopes.value, scope]
    : scopes.value.filter((value) => value !== scope)
}

const close = () => {
  emit('update:open', false)
}

// Сброс на закрытии, а не на открытии: пока диалог виден, значение ключа
// должно оставаться на экране, даже если родитель перерисовался.
watch(
  () => props.open,
  (open) => {
    if (open) return

    name.value = ''
    ttl.value = 'API_KEY_TTL_90_DAYS'
    scopes.value = [...MCP_PRESET]
    secret.value = ''
    copied.value = false
  },
)

const submit = async () => {
  if (!canSubmit.value) return

  isSaving.value = true

  try {
    const created = await createApiKey({
      name: name.value.trim(),
      ttl: ttl.value,
      scopes: scopes.value,
    })

    secret.value = created.secret
    emit('issued')
  } catch (err) {
    toast.error(errorMessage(err, t('settings.integrations.dialog.errors.create')))
  } finally {
    isSaving.value = false
  }
}

const copySecret = async () => {
  try {
    await navigator.clipboard.writeText(secret.value)
    copied.value = true
  } catch {
    toast.error(t('settings.integrations.errors.copy'))
  }
}
</script>

<template>
  <AppModal
    :model-value="open"
    :title="secret ? t('settings.integrations.dialog.doneTitle') : t('settings.integrations.dialog.title')"
    @update:model-value="emit('update:open', $event)"
  >
    <!-- Значение ключа: показывается один раз и больше нигде -->
    <template v-if="secret">
      <p class="dialog__note">{{ t('settings.integrations.dialog.secretHint') }}</p>

      <div class="dialog__secret">
        <code class="dialog__secret-value">{{ secret }}</code>
        <AppButton variant="secondary" size="small" @click="copySecret">
          <component :is="copied ? Check : Copy" :size="14" />
          {{ copied ? t('settings.integrations.connect.copied') : t('settings.integrations.connect.copy') }}
        </AppButton>
      </div>

      <!-- Ключ уже на экране: напомнить, куда его не класть, надо здесь, а не в доке -->
      <p class="dialog__warning">{{ t('settings.integrations.dialog.secretWarning') }}</p>
    </template>

    <template v-else>
      <label class="dialog__label" for="api-key-name">
        {{ t('settings.integrations.dialog.name') }}
      </label>
      <AppTextField
        id="api-key-name"
        v-model="name"
        :placeholder="t('settings.integrations.dialog.namePlaceholder')"
      />

      <p class="dialog__label dialog__label--section">{{ t('settings.integrations.dialog.access') }}</p>

      <div class="dialog__scopes">
        <AppCheckbox
          v-for="scope in KEY_SCOPES"
          :key="scope"
          :model-value="scopes.includes(scope)"
          :label="t(`settings.integrations.dialog.scopes.${scope}`)"
          @update:model-value="toggleScope(scope, $event)"
        />
      </div>

      <p class="dialog__hint">{{ t('settings.integrations.dialog.scopeHint') }}</p>

      <p class="dialog__label dialog__label--section">{{ t('settings.integrations.dialog.lifetime') }}</p>
      <AppSegmentedControl
        v-model="ttl"
        :options="ttlOptions"
        size="small"
        :aria-label="t('settings.integrations.dialog.lifetime')"
      />
    </template>

    <template #footer>
      <AppButton variant="secondary" @click="close">
        {{ secret ? t('settings.integrations.dialog.done') : t('settings.cancel') }}
      </AppButton>
      <AppButton v-if="!secret" :disabled="!canSubmit || isSaving" @click="submit">
        {{ t('settings.integrations.dialog.submit') }}
      </AppButton>
    </template>
  </AppModal>
</template>

<style scoped>
/* Микро-заголовок поля: тот же приём, что у разделов экрана интеграций */
.dialog__label {
  display: block;
  margin: 0 0 var(--spacing-2);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.dialog__warning {
  margin: var(--spacing-3) 0 0;
  padding: var(--spacing-3);
  font-size: var(--font-size-xs);
  color: var(--color-warning-strong);
  background: var(--color-warning-tint);
  border-radius: var(--radius-md);
}

.dialog__label--section {
  margin-top: var(--spacing-5);
}

.dialog__scopes {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
}

.dialog__hint,
.dialog__note {
  margin: var(--spacing-3) 0 0;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}

.dialog__note {
  margin: 0 0 var(--spacing-4);
  font-size: var(--font-size-sm);
}

.dialog__secret {
  display: flex;
  gap: var(--spacing-3);
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-3);
  background: var(--color-bg-muted);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
}

.dialog__secret-value {
  font-family: var(--font-family-mono);
  font-size: var(--font-size-xs);
  color: var(--color-text-primary);
  word-break: break-all;
}
</style>
